import { EventEmitter } from '@angular/core';
import { getBorderColorFromPolygonColor } from '@app/utils/_msf';
import { IPolygonColor } from '@components/request-map/request-map.component2';
import {TranslateService} from '@ngx-translate/core';
import {
  doSegmentsIntersect,
  isSelfIntersecting,
  olCalculateAreaFromPolygon,
  olCalculateAreaFromPolygonInHectares
} from '@app/utils/polygons.utils';
import {MessageService} from 'primeng/api';

declare var ol: any;


export interface PolygonCallbacks {
  coordinatesChange: EventEmitter<{ lat: number; lng: number }[]>;
  polygonAreaChange: EventEmitter<number>;
  geometryChange: EventEmitter<string>;
  validationError: EventEmitter<string | null>;
  boundaryViolation: EventEmitter<string>;
  canSave: EventEmitter<boolean>;
}

export class PolygonManager {
  private drawInteraction: any;
  private modifyInteraction: any;
  private isDrawing = false;
  private onModifyEndCallback: ((feature: any) => void) | null = null;
  private _allowDrawUnderSibling = true;
  private _allowSelfIntersection = false;

  /** Optional label to display on top of the main polygon */
  polygonName: string | null | undefined = null;

  /** Optional second crop colour drawn as diagonal hatch on top of the base fill (crop-IA / acceptance modal). */
  private stripeOverlayColor: string | null = null;

  /** When false, the main polygon renders as outline only (transparent fill). */
  private fillVisible = true;

  setStripeOverlayColor(stripe: string | null | undefined): void {
    this.stripeOverlayColor = stripe && String(stripe).trim() ? String(stripe).trim() : null;
  }

  private static createDiagonalStripePattern(baseColor: string, stripeColor: string): CanvasPattern | null {
    if (typeof document === 'undefined') {
      return null;
    }
    const size = 12;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    ctx.fillStyle = baseColor;
    ctx.globalAlpha = 1;
    ctx.fillRect(0, 0, size, size);
    ctx.strokeStyle = stripeColor;
    ctx.lineWidth = 4;
    ctx.lineCap = 'butt';
    ctx.setLineDash([4, 6]);
    ctx.beginPath();
    ctx.moveTo(-1, size + 1);
    ctx.lineTo(size + 1, -1);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(4, size + 2);
    ctx.lineTo(size + 2, 4);
    ctx.stroke();
    ctx.setLineDash([]);
    const pat = ctx.createPattern(canvas, 'repeat');
    return pat;
  }

  /** Build an ol style for the main polygon, optionally including a label */
  private buildPolygonStyle(fillColor: string, label?: string | null): any {
    const stripe = this.stripeOverlayColor;
    let fill: string | CanvasPattern = this.fillVisible ? fillColor : 'rgba(0, 0, 0, 0)';
    if (stripe && this.fillVisible) {
      const pat = PolygonManager.createDiagonalStripePattern(fillColor, stripe);
      if (pat) {
        fill = pat;
      }
    }
    const borderColor = getBorderColorFromPolygonColor(typeof fillColor === 'string' ? fillColor : '#3388ff');
    const styles: any[] = [
      new ol.style.Style({
        stroke: new ol.style.Stroke({ color: borderColor, width: 2 }),
        fill: new ol.style.Fill({ color: fill as any }),
      }),
    ];
    if (label) {
      styles.push(
        new ol.style.Style({
          text: new ol.style.Text({
            text: label,
            font: 'bold 13px Arial',
            fill: new ol.style.Fill({ color: '#ffffff' }),
            stroke: new ol.style.Stroke({ color: '#333333', width: 3 }),
            placement: 'point',
            textAlign: 'center',
            textBaseline: 'middle',
            overflow: true,
          }),
        })
      );
    }
    return styles.length === 1 ? styles[0] : styles;
  }

  constructor(
    private getMap: () => any,
    private getVectorSource: () => any,
    private polygonColor: string,
    private callbacks: PolygonCallbacks,
    private isInsideParent: (coord: number[]) => boolean,
    private isPolygonInsideParent: (geometry: any) => boolean,
    private clipToParent: (coord: number[]) => number[],
    private translate: TranslateService,
    private isInsideSibling?: (coord: number[]) => { inside: boolean; siblingId?: string | number },
    private messageService?: MessageService,
    private doesPolygonOverlapSibling?: (geometry: any) => { overlaps: boolean; siblingIds: (string | number)[] }
  ) {}

  /**
   * Définit si le dessin est autorisé sous/sur les polygones voisins
   */
  setAllowDrawUnderSibling(allow: boolean): void {
    this._allowDrawUnderSibling = allow;
  }

  /**
   * Définit si l'auto-intersection du polygone est autorisée
   */
  setAllowSelfIntersection(allow: boolean): void {
    this._allowSelfIntersection = allow;
  }



  /**
   * Vérifie si le polygone en cours de création se croise lui-même
   */
  private checkSelfIntersectionDuringDraw(newCoord: number[], existingCoords: number[][]): boolean {
    if (existingCoords.length < 3) {
      return false;
    }

    // Créer le nouveau segment (du dernier point au nouveau point)
    const lastCoord = existingCoords[existingCoords.length - 1];

    // Vérifier si le nouveau segment croise les segments existants (sauf le dernier)
    for (let i = 0; i < existingCoords.length - 2; i++) {
      if (doSegmentsIntersect(
        lastCoord, newCoord,
        existingCoords[i], existingCoords[i + 1]
      )) {
        return true;
      }
    }

    return false;
  }

  /**
   * Vérifie si le polygone chevauche un sibling (pour utilisation dans drawend et modifyend)
   */
  private checkPolygonOverlapsSibling(geometry: any): boolean {
    if (this._allowDrawUnderSibling || !this.doesPolygonOverlapSibling) {
      return false;
    }

    const overlapResult = this.doesPolygonOverlapSibling(geometry);
    if (overlapResult.overlaps) {
      const errorMessage = this.translate.instant('ERROR_POLYGON_INSIDE_EXISTING_POLYGON');
      this.callbacks.validationError.emit(errorMessage);

      if (this.messageService) {
        // this.messageService.add({
        //   severity: 'error',
        //   summary: this.translate.instant('ERROR'),
        //   detail: errorMessage,
        //   life: 5000
        // });
      }
      return true;
    }
    return false;
  }

  /**
   * Vérifie si le dessin est autorisé à cette coordonnée (en tenant compte des siblings)
   */
  private isDrawingAllowedAtCoordinate(coord: number[], hasParent: boolean): boolean {
    // Vérifier d'abord si on est dans le parent (si requis)
    if (hasParent && !this.isInsideParent(coord)) {

      return false;
    }

    // Si allowDrawUnderSibling est true, on ne vérifie pas les siblings
    if (this._allowDrawUnderSibling) {
      return true;
    }

    // Vérifier si on est dans un sibling
    if (this.isInsideSibling) {

      const siblingCheck = this.isInsideSibling(coord);
      if (siblingCheck.inside) {
        const errorMessage = this.translate.instant('ERROR_POLYGON_INSIDE_EXISTING_POLYGON');

        if (this.messageService){
         this.messageService.add({
           severity: 'error',
           summary: this.translate.instant('ERROR'),
           detail: errorMessage,
           life: 5000
         });
       }
        return false;
      }
    }

    return true;
  }

  /**
   * Définit le callback appelé après chaque modification de polygone
   */
  setOnModifyEndCallback(callback: (feature: any) => void): void {
    this.onModifyEndCallback = callback;
  }

  updatePolygonColor(color: string): void {
    this.polygonColor = color;
    const vectorSource = this.getVectorSource();

    vectorSource?.getFeatures().forEach((feature: any) => {
      if (feature.getGeometry().getType() === 'Polygon') {
        feature.setStyle(this.buildPolygonStyle(color, this.polygonName));
      }
    });
  }

  /** Toggle the main polygon fill (outline only when false). Parent/sibling polygons are untouched. */
  setPolygonFillVisible(visible: boolean): void {
    this.fillVisible = visible;
    const vectorSource = this.getVectorSource();

    vectorSource?.getFeatures().forEach((feature: any) => {
      const type = feature.get('type');
      if (
        feature.getGeometry()?.getType() === 'Polygon' &&
        type !== 'parentPolygon' &&
        type !== 'siblingPolygon'
      ) {
        feature.setStyle(this.buildPolygonStyle(this.polygonColor, this.polygonName));
      }
    });
  }

  startPolygonDrawing(hasParent: boolean): void {
    const map = this.getMap();
    const vectorSource = this.getVectorSource();

    if (this.drawInteraction) {
      map.removeInteraction(this.drawInteraction);
    }

    const geometryFunction = hasParent
      ? (coordinates: any, geometry: any) => {
          if (!geometry) {
            geometry = new ol.geom.Polygon([]);
          }
          const clippedCoords = coordinates[0].map((coord: number[]) =>
            this.clipToParent(coord)
          );

        if (clippedCoords.length > 0 && coordinates[0].length > 1) {
          const first = clippedCoords[0];
          const last = clippedCoords[clippedCoords.length - 1];
          if (first[0] !== last[0] || first[1] !== last[1]) {
            clippedCoords.push([first[0], first[1]]);
          }
        }
        geometry.setCoordinates([clippedCoords]);
        return geometry;
        }
      : undefined;

    // Variable pour stocker les coordonnées actuelles du polygone en cours de dessin
    let currentDrawingCoords: number[][] = [];

    this.drawInteraction = new ol.interaction.Draw({
      source: vectorSource,
      type: 'Polygon',
      geometryFunction: geometryFunction,
      condition: (evt: any) => {
        // Vérification de base (parent et sibling)
        if (!this.isDrawingAllowedAtCoordinate(evt.coordinate, hasParent)) {
          return false;
        }

        // Vérifier l'auto-intersection pendant le dessin
        if (!this._allowSelfIntersection && currentDrawingCoords.length >= 3) {
          if (this.checkSelfIntersectionDuringDraw(evt.coordinate, currentDrawingCoords)) {
            const errorMessage = this.translate.instant('ERROR_POLYGON_SELF_INTERSECTING');

            if (this.messageService) {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('ERROR'),
                detail: errorMessage,
                life: 3000
              });
            }
            return false;
          }
        }

        return true;
      }
    });

    this.drawInteraction.on('drawstart', (event: any) => {
      const polygon = event.feature;
      polygon.set('type', 'polygon');
      currentDrawingCoords = []; // Réinitialiser


      polygon.getGeometry().on('change', (geomEvt: any) => {
        const coords = geomEvt.target.getCoordinates()[0];
        currentDrawingCoords = coords.slice(0, -1); // Exclure le point de fermeture temporaire

        if (coords.length < 3) return;

        const areaInHectares = olCalculateAreaFromPolygonInHectares(coords, ol)
        const fillColor = this.polygonColor;
        const borderColor = getBorderColorFromPolygonColor(fillColor);

        polygon.setStyle(new ol.style.Style({
          stroke: new ol.style.Stroke({ color: borderColor, width: 2 }),
          fill: new ol.style.Fill({ color: fillColor }),
          text: new ol.style.Text({
            text: `${areaInHectares.toFixed(2)} ${this.translate.instant('Ha')}`,
            font: 'bold 14px Arial',
            fill: new ol.style.Fill({
              color: '#000000'
            }),
            stroke: new ol.style.Stroke({
              color: '#ffffff',
              width: 3
            }),
            offsetY: 0,
            placement: 'point',
            textAlign: 'center',
            textBaseline: 'middle',
            overflow: true
          })
        }));

        vectorSource.getFeatures()
          .filter((f: any) => f.get('type') === 'vertex')
          .forEach((f: any) => vectorSource.removeFeature(f));

        coords.forEach((coord: any) => {
          const pointFeature = new ol.Feature({
            geometry: new ol.geom.Point(coord),
            type: 'vertex'
          });
          vectorSource.addFeature(pointFeature);
        });

        const latlngs = coords.map((c: any) => {
          const lonLat = ol.proj.toLonLat(c);
          return { lat: lonLat[1], lng: lonLat[0] };
        });
        this.callbacks.coordinatesChange.emit(latlngs);

        // Émettre la superficie en temps réel pendant le dessin
        this.callbacks.polygonAreaChange.emit(areaInHectares);
      });
    });

    this.drawInteraction.on('drawend', (event: any) => {
      this.isDrawing = false;
      map.removeInteraction(this.drawInteraction);

      const drawnGeometry = event.feature.getGeometry();
      const coords = drawnGeometry.getCoordinates()[0];

      // Vérifier l'auto-intersection si non autorisée
      if (!this._allowSelfIntersection && isSelfIntersecting(coords)) {
        this.callbacks.canSave.emit(false)
        const errorMessage = this.translate.instant('ERROR_POLYGON_SELF_INTERSECTING');
        this.callbacks.validationError.emit(errorMessage);

        if (this.messageService) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('ERROR'),
            detail: errorMessage,
            life: 5000
          });
        }

        vectorSource.removeFeature(event.feature);
        // Nettoyer les vertex
        vectorSource.getFeatures()
          .filter((f: any) => f.get('type') === 'vertex')
          .forEach((f: any) => vectorSource.removeFeature(f));
        return;
      }

      // Vérifier le chevauchement avec les siblings
      if (this.checkPolygonOverlapsSibling(drawnGeometry)) {
        vectorSource.removeFeature(event.feature);
        // Nettoyer les vertex
        vectorSource.getFeatures()
          .filter((f: any) => f.get('type') === 'vertex')
          .forEach((f: any) => vectorSource.removeFeature(f));
        return;
      }

      if (hasParent && !this.isPolygonInsideParent(drawnGeometry)) {
        this.callbacks.boundaryViolation.emit('Polygon must be drawn inside the parent boundary');
        vectorSource.removeFeature(event.feature);
        return;
      }

      this.enableEditPolygon(true, hasParent);
      this.savePolygon(event?.feature, hasParent);
    });

    this.callbacks.canSave.emit(true)

    map.addInteraction(this.drawInteraction);
  }

  stopPolygonDrawing(): void {
    const map = this.getMap();
    const vectorSource = this.getVectorSource();

    if (this.drawInteraction) {
      map?.removeInteraction(this.drawInteraction);
      this.drawInteraction = null;
    }

    vectorSource?.clear();
  }

  enableEditPolygon(drawingMode: boolean, hasParent: boolean): void {
    if (!drawingMode) return;

    const map = this.getMap();
    const vectorSource = this.getVectorSource();
    if (!vectorSource) return;

    if (this.modifyInteraction) {
      map.removeInteraction(this.modifyInteraction);
    }

    this.modifyInteraction = new ol.interaction.Modify({
      source: vectorSource,
      condition: (evt: any) => {
        return this.isDrawingAllowedAtCoordinate(evt.coordinate, hasParent);
      }
    });

    map.addInteraction(this.modifyInteraction);

    this.modifyInteraction.on('modifystart', (event: any) => {
      event.features.forEach((feature: any) => {
        if (feature.getGeometry().getType() === 'Polygon') {
          feature.set('originalCoords', feature.getGeometry().getCoordinates());
        }
      });
    });

    this.modifyInteraction.on('modifyend', (event: any) => {
      const features = event.features.getArray();
      features.forEach((feature: any) => {
        if (feature.getGeometry().getType() === 'Polygon') {
          const geometry = feature.getGeometry();
          const coords = geometry.getCoordinates()[0];

          // Vérifier l'auto-intersection si non autorisée
          if (!this._allowSelfIntersection && isSelfIntersecting(coords)) {
            this.callbacks.canSave.emit(false)

            const originalCoords = feature.get('originalCoords');
            if (originalCoords) {
              // geometry.setCoordinates(originalCoords);
              const errorMessage = this.translate.instant('ERROR_POLYGON_SELF_INTERSECTING');
              this.callbacks.validationError.emit(errorMessage);

              if (this.messageService) {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.instant('ERROR'),
                  detail: errorMessage,
                  life: 5000
                });
              }
            }
            return;
          }

          // Vérifier le chevauchement avec les siblings
          if (this.checkPolygonOverlapsSibling(geometry)) {
            const originalCoords = feature.get('originalCoords');
            if (originalCoords) {
              geometry.setCoordinates(originalCoords);
            }
            return;
          }

          if (hasParent && !this.isPolygonInsideParent(geometry)) {
            const originalCoords = feature.get('originalCoords');
            if (originalCoords) {
              geometry.setCoordinates(originalCoords);
              this.callbacks.boundaryViolation.emit('Cannot modify polygon outside the parent boundary');
            }
            return;
          }

          this.savePolygon(feature, hasParent);

          if (this.onModifyEndCallback) {
            this.onModifyEndCallback(feature);
          }
          this.callbacks.canSave.emit(true)

        }
      });
    });
  }

  disableEditPolygon(): void {
    const map = this.getMap();
    if (this.modifyInteraction) {
      map?.removeInteraction(this.modifyInteraction);
      this.modifyInteraction = null;
    }
  }

  savePolygon(feature: any, hasParent: boolean): string | null {
    if (!feature) return null;

    const geometry = feature.getGeometry();

    if (hasParent && !this.isPolygonInsideParent(geometry)) {
      this.callbacks.boundaryViolation.emit('Cannot save polygon outside the parent boundary');
      return null;
    }

    const coords = geometry.getCoordinates()[0];
    const firstCoord = coords[0];
    const isProjected = Math.abs(firstCoord[0]) > 180 || Math.abs(firstCoord[1]) > 90;

    let realCoords = isProjected
      ? coords.map((coord: [number, number]) => ol.proj.toLonLat(coord))
      : [...coords];

    const first = realCoords[0];
    const last = realCoords[realCoords.length - 1];

    if (first[0] !== last[0] || first[1] !== last[1]) {
      realCoords.push([first[0], first[1]]);
    }

    if (realCoords.length < 4) {
      this.callbacks.validationError.emit('Polygon must have at least 4 points');
      return null;
    }

    const geometryWKT = `POLYGON ((${realCoords
      .map((coord: [number, number]) => `${coord[0].toFixed(6)} ${coord[1].toFixed(6)}`)
      .join(', ')}))`;

    this.callbacks.polygonAreaChange.emit(olCalculateAreaFromPolygon(JSON.stringify(realCoords), ol));
    this.callbacks.geometryChange.emit(geometryWKT);
    this.callbacks.validationError.emit(null);

    return geometryWKT;
  }

  drawPolygonFromJson(polygonJson: string, color?: string, initialArea?: number | null): void {
    const vectorSource = this.getVectorSource();
    const map = this.getMap();

    if (!polygonJson || !vectorSource || !map) return;

    // 1. Nettoyage des anciens polygones
    const features = vectorSource.getFeatures();
    features.forEach((feature: any) => {
      const type = feature.getGeometry().getType();
      if (type === 'Polygon' || type === 'MultiPolygon') {
        vectorSource.removeFeature(feature);
      }
    });

    try {
      // 2. Utilisation du format WKT pour lire la chaîne
      const wktFormat = new ol.format.WKT();

      // readFeature transforme directement la chaîne WKT en Feature OpenLayers
      // et gère la projection de EPSG:4326 vers celle de la carte (généralement EPSG:3857)
      const polygonFeature = wktFormat.readFeature(polygonJson, {
        dataProjection: 'EPSG:4326',
        featureProjection: map.getView().getProjection()
      });

      const geometry = polygonFeature.getGeometry();
      if (!geometry) return;

      const extent = geometry.getExtent();

      // 3. Application du style
      const fillColor = color || this.polygonColor;
      polygonFeature.setStyle(this.buildPolygonStyle(fillColor, this.polygonName));

      // 4. Ajout à la source et ajustement de la vue
      vectorSource.addFeature(polygonFeature);

      map.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        maxZoom: 17
      });

      // 5. Calcul de l'aire
      const areaToEmit = (initialArea != null) ? initialArea : olCalculateAreaFromPolygon(polygonJson, ol);
      if (this.callbacks && this.callbacks.polygonAreaChange) {
        this.callbacks.polygonAreaChange.emit(areaToEmit);
      }

    } catch (error) {
      console.error('Error drawing polygon from WKT:', error, polygonJson);
    }
  }

  drawPolygonFromJsonAndFitView(polygonArray: IPolygonColor[]): void {
    const vectorSource = this.getVectorSource();
    const map = this.getMap();

    if (!polygonArray || polygonArray.length === 0 || !vectorSource || !map) return;

    // 1. Clear existing features
    const features = vectorSource.getFeatures();
    features.forEach((feature: any) => {
      const type = feature.getGeometry().getType();
      if (type === 'Polygon' || type === 'MultiPolygon') {
        vectorSource.removeFeature(feature);
      }
    });

    // 2. Initialize the WKT formatter
    const wktFormat = new ol.format.WKT();

    // Use OpenLayers extent utility for cleaner math
    let totalExtent = ol.extent.createEmpty();
    let hasValidFeatures = false;

    polygonArray.forEach(polygonItem => {
      try {
        // 3. Parse the WKT string directly
        // This automatically handles the "POLYGON ((...))" format and holes
        const polygonFeature = wktFormat.readFeature(polygonItem.polygon, {
          dataProjection: 'EPSG:4326',
          featureProjection: map.getView().getProjection()
        });

        const geometry = polygonFeature.getGeometry();
        if (!geometry) return;

        // 4. Style and Add
        const fillColor = polygonItem.color || this.polygonColor;
        polygonFeature.setStyle(this.buildPolygonStyle(fillColor, this.polygonName));

        vectorSource.addFeature(polygonFeature);

        // Expand the total extent to include this new feature
        ol.extent.extend(totalExtent, geometry.getExtent());
        hasValidFeatures = true;

      } catch (error) {
        console.error('Error parsing WKT polygon string:', error, polygonItem.polygon);
      }
    });

    // 5. Fit the map view to the combined extent of all new polygons
    if (hasValidFeatures) {
      map.getView().fit(totalExtent, {
        padding: [50, 50, 50, 50],
        maxZoom: 17,
        duration: 500
      });
    }
  }


  clearPolygons(): void {
    const vectorSource = this.getVectorSource();
    if (!vectorSource) return;

    const features = vectorSource.getFeatures();
    features.forEach((feature: any) => {
      if (feature.getGeometry().getType() === 'Polygon') {
        vectorSource.removeFeature(feature);
      }
    });
  }





}
